import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Phone, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiErr } from "@/lib/api";

const fmtINR = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function AdminTelephony() {
  const [nums, setNums] = useState(null);
  const [clients, setClients] = useState([]);
  const [show, setShow] = useState(false);
  const load = () => adminApi.get("/admin/telephony/numbers").then((r) => setNums(r.data)).catch(() => setNums([]));
  useEffect(() => { load(); adminApi.get("/admin/clients").then((r) => setClients(r.data)); }, []);
  const assign = async (id, tenant_id) => { try { await adminApi.post(`/admin/telephony/numbers/${id}/assign`, { tenant_id }); toast.success("Number assigned"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const release = async (id) => { try { await adminApi.post(`/admin/telephony/numbers/${id}/release`); toast.success("Number released"); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-2xl font-bold font-display">Telephony Management</h1><p className="text-sm text-slate-500">Manage the platform's Plivo number pool and client assignments.</p></div>
        <Button style={{ background: "#7C3AED" }} onClick={() => setShow(true)} data-testid="tel-buy-btn"><Plus className="w-4 h-4 mr-1" />Add Number</Button>
      </div>
      <Card className="border shadow-none overflow-x-auto">
        {!nums ? <Loader /> : nums.length === 0 ? <Empty text="No numbers in the pool." /> :
          <table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-400 border-b"><th className="px-4 py-2">Number</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">Cost/mo</th><th className="px-4 py-2">Capabilities</th><th className="px-4 py-2">Assigned To</th><th className="px-4 py-2">Status</th><th className="px-4 py-2 text-right">Actions</th></tr></thead>
            <tbody>{nums.map((n) => (
              <tr key={n.id} className="border-b" data-testid={`tel-row-${n.id}`}>
                <td className="px-4 py-3 font-mono font-medium flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" />{n.number}</td>
                <td className="px-4 py-3 capitalize">{n.type.replace("_", "-")}</td>
                <td className="px-4 py-3">{fmtINR(n.monthly_cost_inr)}</td>
                <td className="px-4 py-3 text-slate-500">{[n.capabilities?.voice && "Voice", n.capabilities?.sms && "SMS"].filter(Boolean).join(", ")}</td>
                <td className="px-4 py-3">{n.assigned_company || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${n.status === "assigned" ? "bg-emerald-100 text-emerald-700" : n.status === "reserved" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{n.status}</span></td>
                <td className="px-4 py-3 text-right">
                  {n.status === "available" ?
                    <Select onValueChange={(v) => assign(n.id, v)}><SelectTrigger className="h-8 w-36 inline-flex" data-testid={`tel-assign-${n.id}`}><SelectValue placeholder="Assign to..." /></SelectTrigger><SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent></Select> :
                    <Button size="sm" variant="outline" className="h-7 text-red-600" onClick={() => release(n.id)} data-testid={`tel-release-${n.id}`}>Release</Button>}
                </td>
              </tr>
            ))}</tbody></table>}
      </Card>
      <AddNumberDialog open={show} onClose={() => setShow(false)} onDone={() => { setShow(false); load(); }} />
    </div>
  );
}

function AddNumberDialog({ open, onClose, onDone }) {
  const [f, setF] = useState({ number: "", type: "local", monthly_cost_inr: "150" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.number) { toast.error("Enter a number"); return; }
    setBusy(true);
    try { await adminApi.post("/admin/telephony/numbers", { ...f, monthly_cost_inr: Number(f.monthly_cost_inr) }); toast.success("Number added to pool"); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}><DialogContent data-testid="add-number-dialog">
      <DialogHeader><DialogTitle>Add Plivo Number</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Number</Label><Input value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} placeholder="+9122..." data-testid="number-input" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Type</Label><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="local">Local</SelectItem><SelectItem value="toll_free">Toll-Free</SelectItem></SelectContent></Select></div>
          <div><Label>Cost/mo (₹)</Label><Input type="number" value={f.monthly_cost_inr} onChange={(e) => setF({ ...f, monthly_cost_inr: e.target.value })} /></div>
        </div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button style={{ background: "#7C3AED" }} onClick={submit} disabled={busy} data-testid="number-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function Loader() { return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: "#7C3AED" }} /></div>; }
function Empty({ text }) { return <div className="p-10 text-center text-sm text-slate-400">{text}</div>; }
